import React, { useCallback, useEffect, useMemo, useState } from "react"
import Alert from "@mui/material/Alert"
import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Switch from "@mui/material/Switch"
import TextField from "@mui/material/TextField"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import LoadingScreen from "components/common/LoadingScreen"
import PageContent from "components/common/LayoutComponents/PageContent"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import MediaManager from "components/common/MediaManager"
import MediaContentDisplay from "components/common/MediaManager/MediaContentDisplay"
import { UsePermissionVerification } from "components/customHooks"
import { PERMISSIONS } from "constants/permission"
import { useSnackbar } from "notistack"
import { addSystemConfigs, editSystemConfigs, getSystemConfigByKey } from "services/systemConfig"
import { verifyResponse } from "utils/httpRequest"
import { NeuroFitnessGamesWrapper } from "./NeuroFitnessGames.style"

const CONFIG_KEY = "neuro_fitness_games"

const GAME_DEFINITIONS = [
  ["processing-speed", "Readiness Radar", "Track alertness, response speed, and lapses with a short readiness challenge.", true, true],
  ["impulse-control", "Impulse Pause", "Practice pause-and-respond control by acting on go cues and withholding on no-go cues.", true, true],
  ["working-memory", "Memory Ladder", "Build consistency with an updating challenge that asks you to keep recent items in mind.", true, true],
  ["flanker", "Focus Filter", "Strengthen focus under conflicting signals by responding to the center cue.", true, true],
  ["task-switching", "Rule Flip", "Practice shifting between rules while tracking switch cost and accuracy.", true, true],
  ["visual-search", "Signal Search", "Train fast, accurate scanning by finding targets among distractors under time pressure.", true, true],
  ["rule-discovery", "Pattern Shift", "Discover hidden sorting rules, adapt when they change, and learn from feedback.", true, true],
  ["mental-rotation", "Rotate Match", "Compare objects at different angles and decide whether they match or are mirrored.", false, true],
  ["spatial-navigation", "Find Your Way Back", "Study short routes through a grid, then identify the path that leads back to the goal.", false, true],
]

const ADMIN_GAME_GUIDES = {
  "processing-speed": {
    paradigm: "Psychomotor Vigilance Task",
    howItWorks: "The player waits for an unpredictable signal and responds as quickly as possible without tapping early.",
    benefits: "Practices sustained attention, readiness, and consistent responding during a short focused task.",
    measures: "Reaction time, unusually slow responses (lapses), early taps, and response consistency.",
    important: "Results can be affected by sleep, stress, distractions, device performance, and internet/browser conditions. It is not a medical fatigue test.",
  },
  "impulse-control": {
    paradigm: "Go/No-Go",
    howItWorks: "The player responds to frequent Go cues and holds back when a less-common No-Go cue appears.",
    benefits: "Practices pausing before acting and maintaining attention when the correct response changes.",
    measures: "Incorrect responses to Stop cues, missed Go cues, accuracy, reaction time, and consistency.",
    important: "A lower score does not mean someone lacks self-control in daily life. It describes performance on this specific task.",
  },
  "working-memory": {
    paradigm: "N-back",
    howItWorks: "The player watches a sequence and decides whether each item matches one shown a certain number of steps earlier.",
    benefits: "Practices holding recent information in mind, updating it, and staying focused across a sequence.",
    measures: "Correct matches, missed matches, false alarms, response speed, and the highest sustained difficulty.",
    important: "The challenge becomes more demanding as the comparison moves farther back. Claims should stay focused on this task and closely related memory tasks.",
  },
  flanker: {
    paradigm: "Eriksen Flanker",
    howItWorks: "The player responds to the center arrow while ignoring surrounding arrows that may point the same or opposite way.",
    benefits: "Practices selective attention and responding accurately when nearby information is distracting.",
    measures: "Accuracy and speed when surrounding arrows agree versus conflict, including the interference cost.",
    important: "The game measures performance under visual conflict; it does not measure a person's beliefs, worth, or general ability.",
  },
  "task-switching": {
    paradigm: "Task Switching",
    howItWorks: "The player applies one simple rule, then changes to another rule when the on-screen cue switches.",
    benefits: "Practices shifting attention between instructions while maintaining speed and accuracy.",
    measures: "Accuracy and reaction time on repeated rules versus switched rules, including switch cost.",
    important: "Some slowdown after a rule change is normal. The useful signal is the player's own trend over repeated sessions.",
  },
  "visual-search": {
    paradigm: "Visual Search",
    howItWorks: "The player scans a field of similar items and decides whether a target is present.",
    benefits: "Practices systematic scanning, selective attention, and balancing speed with accuracy.",
    measures: "Accuracy, response time, and how search time changes as the number of items increases.",
    important: "Screen size, vision, display quality, and input method can influence results. Keep visual accessibility in mind when changing artwork.",
  },
  "rule-discovery": {
    paradigm: "Rule Discovery / Card Sorting",
    howItWorks: "The player compares cards, infers the hidden matching rule from feedback, and adapts when the rule changes.",
    benefits: "Practices testing ideas, learning from feedback, and changing strategy when an old approach stops working.",
    measures: "Accuracy, trials needed to identify a rule, repeated use of an outdated rule, and adaptation after a change.",
    important: "Avoid framing errors as failure. Feedback is intentionally part of the learning process and should remain clear and supportive.",
  },
  "mental-rotation": {
    paradigm: "Mental Rotation",
    howItWorks: "The player compares two asymmetric objects shown at different angles and decides whether they are the same object or mirror images.",
    benefits: "Practices mentally transforming shapes and maintaining visuospatial details while making a comparison.",
    measures: "Accuracy, reaction time at each angle, mirror-image accuracy, and how response time changes with rotation angle.",
    important: "This is a task-specific visuospatial exercise, not a measure of IQ. Keep shapes asymmetric so mirrored trials cannot be solved by guessing.",
  },
  "spatial-navigation": {
    paradigm: "Spatial Navigation / Route Memory",
    howItWorks: "The player studies a highlighted path to a goal. After it disappears, the player chooses the directions that retrace the route back to the start.",
    benefits: "Practices route memory, spatial sequencing, and mentally reversing a learned path.",
    measures: "Route accuracy, errors, recall time, and performance as path length increases.",
    important: "This is a simplified 2D route-memory proxy, not a real-world navigation or memory assessment. Longer paths should be introduced gradually.",
  },
}

const defaultGames = () =>
  GAME_DEFINITIONS.map(([id, title, description, enabledForAllUsers, enabledForBetaUsers]) => ({
    id,
    title,
    description,
    thumbnailUrl: "",
    thumbnailResource: null,
    enabledForAllUsers,
    enabledForBetaUsers,
  }))

const parseConfig = (value) => {
  if (!value) return {}
  if (typeof value === "object") return value
  try {
    return JSON.parse(value)
  } catch {
    return {}
  }
}

const normalizeConfig = (value) => {
  const parsed = parseConfig(value)
  const savedById = new Map(
    (Array.isArray(parsed?.games) ? parsed.games : Object.values(parsed?.games || {}))
      .filter((game) => game?.id)
      .map((game) => [game.id, game]),
  )
  return {
    version: Number(parsed?.version || 1),
    games: defaultGames().map((game) => ({ ...game, ...(savedById.get(game.id) || {}), id: game.id })),
  }
}

const mediaUrl = (media) =>
  media?.urlDto?.imgUrl ||
  media?.urlDto?.originalUrl ||
  media?.url ||
  ""

const serializeConfig = (config) => ({
  version: config.version,
  games: config.games.map((game) => ({
    id: game.id,
    title: game.title.trim(),
    description: game.description.trim(),
    thumbnailUrl: game.thumbnailUrl || "",
    thumbnailResourceId: game.thumbnailResourceId || null,
    thumbnailResource: game.thumbnailResource ? {
      id: game.thumbnailResource.id,
      type: game.thumbnailResource.type,
      awsBucket: game.thumbnailResource.awsBucket,
      awsKey: game.thumbnailResource.awsKey,
      url: game.thumbnailResource.url,
      urlDto: game.thumbnailResource.urlDto,
    } : null,
    enabledForAllUsers: Boolean(game.enabledForAllUsers),
    enabledForBetaUsers: Boolean(game.enabledForBetaUsers),
  })),
})

export default function NeuroFitnessGames() {
  const { enqueueSnackbar } = useSnackbar()
  const AccessControl = UsePermissionVerification()
  const [openMediaManager, mediaManager] = MediaManager()
  const [configRow, setConfigRow] = useState(null)
  const [config, setConfig] = useState({ version: 1, games: defaultGames() })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const canView =
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_QUERY_BY_KEY]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_EDIT]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_ADD])
  const canEdit =
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_EDIT]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_ADD])

  const loadConfig = useCallback(async () => {
    if (!canView) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError("")
    try {
      const response = await getSystemConfigByKey(CONFIG_KEY)
      verifyResponse(response)
      const row = response?.data || response
      setConfigRow(row?.id ? row : null)
      setConfig(normalizeConfig(row?.configValueJson ?? row?.configValue))
    } catch (err) {
      console.error("load Neuro-Fitness games config", err)
      setError("Unable to load the Neuro-Fitness game catalog.")
    } finally {
      setLoading(false)
    }
  }, [canView])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  const updateGame = (gameId, changes) => {
    setConfig((previous) => ({
      ...previous,
      games: previous.games.map((game) => game.id === gameId ? { ...game, ...changes } : game),
    }))
  }

  const chooseThumbnail = (game) => {
    openMediaManager((media) => {
      updateGame(game.id, {
        thumbnailResource: media,
        thumbnailResourceId: media?.id || null,
        thumbnailUrl: mediaUrl(media),
      })
    }, "image", "search")
  }

  const handleSave = async () => {
    if (!canEdit) return
    const invalid = config.games.find((game) => !game.title?.trim() || !game.description?.trim())
    if (invalid) {
      setError(`Title and description are required for ${invalid.id}.`)
      return
    }
    setSaving(true)
    setError("")
    const body = {
      name: "Neuro-Fitness games",
      configKey: CONFIG_KEY,
      description: "Editable game presentation, thumbnails, and audience rollout settings.",
      configValue: JSON.stringify(serializeConfig(config)),
    }
    try {
      const response = configRow?.id
        ? await editSystemConfigs({ ...body, id: configRow.id })
        : await addSystemConfigs(body)
      verifyResponse(response)
      enqueueSnackbar("Neuro-Fitness games saved", { variant: "success" })
      await loadConfig()
    } catch (err) {
      console.error("save Neuro-Fitness games config", err)
      setError("Failed to save the Neuro-Fitness game catalog.")
    } finally {
      setSaving(false)
    }
  }

  const rolloutSummary = useMemo(() => ({
    live: config.games.filter((game) => game.enabledForAllUsers).length,
    beta: config.games.filter((game) => !game.enabledForAllUsers && game.enabledForBetaUsers).length,
    off: config.games.filter((game) => !game.enabledForAllUsers && !game.enabledForBetaUsers).length,
  }), [config.games])

  return (
    <PageContent>
      <NeuroFitnessGamesWrapper>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">Neuro-Fitness Games</h1>
            <p className="page-subtitle">
              Edit member-facing game titles, descriptions, thumbnails, and rollout. Game mechanics and scoring remain versioned in code.
            </p>
          </div>
          {canEdit && (
            <Button className="save-btn" variant="contained" disabled={loading || saving} onClick={handleSave}>
              {saving ? "Saving..." : "Save games"}
            </Button>
          )}
        </PageHeading>

        {error ? <Alert severity="error">{error}</Alert> : null}
        <div className="page-surface">
          {!canView ? (
            <div className="page-empty">You do not have permission to view game settings.</div>
          ) : (
            <>
              <Alert severity="info">
                Beta-only games are shown only to members whose <strong>Beta tester</strong> switch is enabled.
              </Alert>
              <div>
                <Chip label={`${rolloutSummary.live} live`} color="success" sx={{ mr: 1 }} />
                <Chip label={`${rolloutSummary.beta} beta only`} color="primary" variant="outlined" sx={{ mr: 1 }} />
                <Chip label={`${rolloutSummary.off} off`} variant="outlined" />
              </div>
              <div className="games-grid">
                {config.games.map((game) => {
                  const status = game.enabledForAllUsers ? "Live" : game.enabledForBetaUsers ? "Beta only" : "Off"
                  const guide = ADMIN_GAME_GUIDES[game.id]
                  return (
                    <article className="game-card" key={game.id}>
                      <div className="game-header">
                        <div>
                          <h2>{game.title || "Untitled game"}</h2>
                          <div className="game-key">{game.id}</div>
                        </div>
                        <Chip
                          size="small"
                          color={game.enabledForAllUsers ? "success" : game.enabledForBetaUsers ? "primary" : "default"}
                          label={status}
                        />
                      </div>
                      <div className="thumbnail-row">
                        <MediaContentDisplay resource={game.thumbnailResource} small />
                        <div className="thumbnail-actions">
                          <Button variant="outlined" disabled={!canEdit} onClick={() => chooseThumbnail(game)}>
                            {game.thumbnailUrl ? "Replace thumbnail" : "Choose or upload thumbnail"}
                          </Button>
                          {game.thumbnailUrl && (
                            <Button
                              color="error"
                              disabled={!canEdit}
                              onClick={() => updateGame(game.id, { thumbnailUrl: "", thumbnailResourceId: null, thumbnailResource: null })}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="fields">
                        <TextField
                          label="Title"
                          value={game.title}
                          disabled={!canEdit}
                          inputProps={{ maxLength: 128 }}
                          onChange={(event) => updateGame(game.id, { title: event.target.value })}
                        />
                        <TextField
                          label="Description"
                          value={game.description}
                          disabled={!canEdit}
                          multiline
                          minRows={3}
                          inputProps={{ maxLength: 1000 }}
                          onChange={(event) => updateGame(game.id, { description: event.target.value })}
                        />
                      </div>
                      {guide && (
                        <Accordion className="admin-guide" disableGutters elevation={0}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div>
                              <strong>Plain-language admin guide</strong>
                              <span>How the game works, its benefits, and how to describe results responsibly.</span>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails>
                            <div className="guide-paradigm">
                              <span>Scientific foundation</span>
                              <strong>{guide.paradigm}</strong>
                            </div>
                            <dl className="guide-grid">
                              <div><dt>How the game works</dt><dd>{guide.howItWorks}</dd></div>
                              <div><dt>What it helps practice</dt><dd>{guide.benefits}</dd></div>
                              <div><dt>What we track</dt><dd>{guide.measures}</dd></div>
                              <div><dt>Important to know</dt><dd>{guide.important}</dd></div>
                            </dl>
                          </AccordionDetails>
                        </Accordion>
                      )}
                      <div className="toggles">
                        <div className="toggle">
                          <div><strong>All users</strong><span>Publish this game to everyone.</span></div>
                          <Switch
                            checked={Boolean(game.enabledForAllUsers)}
                            disabled={!canEdit}
                            onChange={(event) => updateGame(game.id, { enabledForAllUsers: event.target.checked })}
                          />
                        </div>
                        <div className="toggle">
                          <div><strong>Beta users</strong><span>Allow beta testers when not live.</span></div>
                          <Switch
                            checked={Boolean(game.enabledForBetaUsers)}
                            disabled={!canEdit}
                            onChange={(event) => updateGame(game.id, { enabledForBetaUsers: event.target.checked })}
                          />
                        </div>
                      </div>
                    </article>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </NeuroFitnessGamesWrapper>
      {mediaManager}
      <LoadingScreen loading={loading} />
    </PageContent>
  )
}
